/**
 * FeedbackPage, visitor PWA v2 landing.
 *
 * RAG-first layout: the ask card is the hero, suggestion chips scan across a
 * single scrollable row, and operational reporting is a binary OK/Issue row
 * beneath. Detailed per-item status tiles live inside an optional disclosure.
 * "Something else in this building" opens a bottom sheet with building-scope
 * categories that route to the building root rather than just the amenity.
 *
 * Backend contract:
 *   GET  /api/status/:qrCode   → amenity + statusItems + prompts
 *   POST /api/rag/ask          → used by ChatSheet
 *   POST /api/feedback         → with { mode: 'all_good' | 'issue' | 'rating' }
 *   POST /api/status/:qrCode/report (via StatusUpdateModal, unchanged from v1)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applyTenantDefaultLanguage, isRTL } from '@/i18n';
import {
  AlertCircle,
  ArrowRight,
  Building2,
  ChevronDown,
  Lock,
  Mic,
  RotateCw,
  Trophy,
  WifiOff,
  Wrench,
} from 'lucide-react';
import { cn } from '@tendedloop/ui';
import { ChatSheet } from '@/components/ChatSheet';
import { IssueSheet, type IssueOption } from '@/components/IssueSheet';
import { BuildingSheet, type BuildingCategory } from '@/components/BuildingSheet';
import { AnchorChip } from '@/components/AnchorChip';
import { ParkingRecallOverlay } from '@/components/ParkingRecallOverlay';
import { readAnchor, relativeTime, type ParkedSpot } from '@/lib/parking';
import { fetchWithRetry } from '@/lib/fetchWithRetry';
import { FeedbackSkeleton } from '@/components/FeedbackSkeleton';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';
import { StatusGrid, StatusUpdateModal, StatusConfirmation, type StatusItem } from '@/components/status';
import { toast } from '@/hooks/useToast';
import { buildGreeting } from '@/utils/timeGreeting';
import {
  queueFeedback,
  queueStatusReport,
  isOnline,
  getTotalQueueCount,
  syncQueuedFeedback,
  syncQueuedStatusReports,
} from '@/lib/offlineQueue';
import { fetchLanding } from '@/services/landingContext';
import type { LandingResponse } from '@tendedloop/shared';
import { useScene } from '@/hooks/useScene';
import { ForesightHero } from '@/components/ForesightHero';
import { DirectionProvider } from '@/providers/DirectionProvider';
import { DirectionAvatar } from '@/components/direction';
import { BrandBar } from '@/components/BrandBar';
import { apiErrorText } from '@tendedloop/shared/i18n';

// ============================================================================
// Session-scoped "I already reported here" flag
// ============================================================================
// Small helpers, kept close to the component so the storage-key format is
// obvious to anyone reading the component. See `hasIssueThisSession` comment
// below for the design rationale.
const ISSUE_FLAG_TTL_MS = 15 * 60 * 1000; // 15 minutes, long enough to cover
// a refresh mid-visit, short enough
// that a stale tab left open
// overnight doesn't mislead.
const issueFlagKey = (qr: string | undefined) => `tl_issued_${qr ?? ''}`;

function readIssuedFlag(qr: string | undefined): boolean {
  if (!qr || typeof window === 'undefined' || !window.sessionStorage) return false;
  try {
    const raw = window.sessionStorage.getItem(issueFlagKey(qr));
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < ISSUE_FLAG_TTL_MS;
  } catch {
    return false; // Safari private mode / storage quota / similar
  }
}

function writeIssuedFlag(qr: string | undefined): void {
  if (!qr || typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(issueFlagKey(qr), String(Date.now()));
  } catch {
    // Swallow: best-effort. Refreshing this tab will just lose the red tint,
    // which is the same as the pre-fix baseline.
  }
}

// ============================================================================
// Types
// ============================================================================

type ErrorType = 'NOT_FOUND' | 'NOT_CONFIGURED' | 'GENERIC';
type NotConfiguredData = { tenantName?: string | null };

interface AmenityData {
  tenant?: {
    id: string;
    name: string;
    defaultLanguage?: string;
    /** Optional hex (e.g. "#0E7C7B") applied as --tl-accent. Null = default. */
    accentColor?: string | null;
    /** Visual-language direction. CSS variables in directions.css drive the swap. */
    direction?: 'atelier' | 'concierge';
    /** The building's own logo, shown on a slim band at the top. Null = no band. */
    brand?: { name: string | null; logoUrl: string; logoBackground: 'dark' | 'light' } | null;
  };
  amenity: {
    id: string;
    name: string;
    // Translated copy when the tenant opts in, falls back to `name`.
    displayName?: string;
    nameLocale?: string;
    description: string | null;
    amenityType: { id: string; name: string; icon: string | null; anchorable?: boolean };
  };
  building: { id: string; name: string; displayName?: string; nameLocale?: string };
  location: {
    id: string;
    name: string;
    displayName?: string;
    nameLocale?: string;
    level: number | null;
    locationType?: string;
    path?: string;
  };
  statusItems: StatusItem[];
  showPoweredBy?: boolean;
  askEnabled?: boolean;
  prompts?: Array<{
    id: string;
    icon: string | null;
    question: string;
    displayQuestion?: string;
    questionLocale?: string;
  }>;
  parking?: { ttlHours: number };
}

interface ConfirmationState {
  statusType: string;
  newStatus: string;
  taskCreated: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function FeedbackPage() {
  const { qrCode } = useParams<{ qrCode: string }>();
  const { t, i18n } = useTranslation('visitor');
  const apiUrl = import.meta.env.VITE_API_URL || '';

  // Core data
  const [data, setData] = useState<AmenityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<ErrorType>('GENERIC');
  const [notConfiguredData, setNotConfiguredData] = useState<NotConfiguredData>({});

  // Online / queue
  const [online, setOnline] = useState(isOnline());
  const [queuedCount, setQueuedCount] = useState(0);

  // Session-only ops state. `hasIssueThisSession` drives the tinted-red
  // "already reported" affordance on the issue button + its clarifying
  // subline. Persisted to sessionStorage with a 15-minute TTL scoped to this
  // QR so an accidental refresh doesn't make the visitor think their tap
  // failed and re-report. Not localStorage (same-device-tomorrow is a new
  // moment; and a shared device shouldn't bleed state across visitors) and
  // not DB (privacy: anonymous feedback shouldn't grow a per-device tracking
  // row just for a UI hint, the real record is already in the Feedback
  // table).
  const [hasIssueThisSession, setHasIssueThisSession] = useState(() => readIssuedFlag(qrCode));

  const markIssueLogged = useCallback(() => {
    setHasIssueThisSession(true);
    writeIssuedFlag(qrCode);
  }, [qrCode]);

  // Sheets / overlays
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSeed, setChatSeed] = useState<string | null>(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issuePhotoPrompted, setIssuePhotoPrompted] = useState(false);
  const [issueImage, setIssueImage] = useState<File | null>(null);
  const [buildingOpen, setBuildingOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false); // disclosure

  // Status update modal
  const [selectedItem, setSelectedItem] = useState<StatusItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<ConfirmationState | null>(null);

  // Parking recall, localStorage-only anchor + overlay
  const [parkedSpot, setParkedSpot] = useState<ParkedSpot | null>(null);
  const [recallOpen, setRecallOpen] = useState(false);

  // Context-aware landing (AI-composed hero/chips/placeholder). Fetched
  // in parallel with /api/status; swaps in if it arrives before the
  // visitor starts interacting AND within the late-arrival threshold.
  const [landing, setLanding] = useState<LandingResponse | null>(null);
  const visitorInteractedRef = useRef(false);
  // Keyed re-read so AnchorChip changes reflect in the recall row immediately
  const [anchorTick, setAnchorTick] = useState(0);

  // Lobby scene engine — derives the active scene from the landing response.
  // Phase 01 ships 'ask' and 'foresight'; other scenes degrade to 'ask'.
  const { showForesight, dismissForesight } = useScene(landing);

  useEffect(() => {
    if (!data?.tenant) {
      setParkedSpot(null);
      return;
    }
    setParkedSpot(readAnchor(data.tenant.id, data.building.id));
  }, [data, anchorTick]);

  // Greeting (recomputed on language change)
  const greeting = useMemo(() => buildGreeting(t), [t]);

  // ============================================================================
  // Effects
  // ============================================================================

  // Parallel fetch of the AI-composed landing. Fires on every page load
  // alongside /api/status; the static fallback hero renders instantly and
  // swaps to the AI version on arrival (if visitor hasn't interacted and
  // we're within the late-arrival threshold).
  useEffect(() => {
    if (!qrCode) return;
    const pageLoadedAt = Date.now();
    void fetchLanding({ qrCode, language: i18n.language, apiUrl, pageLoadedAt }).then((result) => {
      if (!result.ok) return;
      if (visitorInteractedRef.current) return; // swap-skip rule
      if (Date.now() - pageLoadedAt > 3500) return; // late-arrival belt-and-suspenders
      setLanding(result.response);
    });
  }, [qrCode, i18n.language, apiUrl]);

  useEffect(() => {
    async function fetchData() {
      if (!qrCode) {
        setError(t('errors:qrCode.invalid'));
        setLoading(false);
        return;
      }
      try {
        // Pass the active UI locale so the API can surface translated
        // tenant content (amenity / tracked-item / prompt names) when the
        // tenant opts into content translation. When it doesn't, the
        // endpoint is a no-op and returns source strings.
        const lang = i18n.language?.split('-')[0] ?? '';
        const url = lang
          ? `${apiUrl}/api/status/${qrCode}?lang=${encodeURIComponent(lang)}`
          : `${apiUrl}/api/status/${qrCode}`;
        // Retry on 5xx — Cloud Run cold-starts occasionally 503 for ~1-2s
        // after scale-to-zero. A distracted visitor will tolerate ~3s of
        // "loading" but will bail on an error screen.
        const response = await fetchWithRetry(url);
        const result = await response.json();
        if (!response.ok || !result.success) {
          if (result.code === 'QR_NOT_CONFIGURED') {
            setErrorType('NOT_CONFIGURED');
            setNotConfiguredData({ tenantName: result.data?.tenantName });
            setError('not_configured');
          } else if (response.status === 404) {
            setErrorType('NOT_FOUND');
            setError(t('errors:qrCode.notFound'));
          } else {
            setErrorType('GENERIC');
            setError(t('errors:generic.loadFailed'));
          }
          return;
        }
        setData(result.data);
        applyTenantDefaultLanguage(result.data?.tenant?.defaultLanguage);
        // Apply per-tenant accent color as a CSS variable. Used by the
        // action link, chat-avatar dot, and focus rings. Falls back to the
        // default TendedLoop teal when the tenant hasn't opted in.
        const accent = result.data?.tenant?.accentColor;
        if (accent && /^#[0-9A-Fa-f]{6}$/.test(accent)) {
          document.documentElement.style.setProperty('--tl-accent', accent);
        } else {
          document.documentElement.style.removeProperty('--tl-accent');
        }
        window.gtag?.('event', 'qr_scanned', {
          amenity_type: result.data?.amenity?.amenityType?.name || 'unknown',
        });
      } catch (err) {
        console.error('Failed to fetch amenity:', err);
        setErrorType('GENERIC');
        setError(t('errors:generic.loadFailed'));
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [qrCode, apiUrl, t, i18n.language]);

  useEffect(() => {
    const handleOnline = async () => {
      setOnline(true);
      const count = await getTotalQueueCount();
      if (count > 0) {
        const [s, f] = await Promise.all([syncQueuedStatusReports(apiUrl), syncQueuedFeedback(apiUrl)]);
        const total = s.success + f.success;
        if (total > 0) {
          toast({
            title: t('offline.syncComplete'),
            description: t('offline.syncCompleteMessage', { count: total }),
            variant: 'success',
          });
        }
        setQueuedCount(await getTotalQueueCount());
      }
    };
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    getTotalQueueCount().then(setQueuedCount);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [apiUrl, t]);

  // ============================================================================
  // Feedback submission (shared by all_good / issue paths)
  // ============================================================================

  /**
   * Two-stage presigned upload for a visitor-attached photo.
   * Returns the imageKey + metadata the feedback POST needs, or null if
   * storage isn't configured or the upload failed (we continue without it).
   */
  const uploadPhoto = useCallback(
    async (
      file: File,
    ): Promise<{
      imageKey: string;
      imageMimeType: 'image/jpeg' | 'image/png' | 'image/webp';
      imageFileSize: number;
    } | null> => {
      if (!qrCode) return null;
      try {
        const mimeType = (file.type || 'image/jpeg') as 'image/jpeg' | 'image/png' | 'image/webp';
        const urlRes = await fetch(`${apiUrl}/api/feedback/upload-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ qrCode, mimeType, fileSize: file.size }),
        });
        const urlJson = await urlRes.json();
        if (!urlRes.ok || !urlJson?.success || !urlJson.data?.uploadUrl) return null;
        const { uploadUrl, imageKey } = urlJson.data as { uploadUrl: string; imageKey: string };
        // Do NOT set Content-Length, browsers manage it and the signed URL
        // already scopes it via x-goog-content-length-range.
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': mimeType },
        });
        if (!putRes.ok) return null;
        return { imageKey, imageMimeType: mimeType, imageFileSize: file.size };
      } catch {
        return null;
      }
    },
    [apiUrl, qrCode],
  );

  const submitFeedback = useCallback(
    async (payload: {
      mode: 'all_good' | 'issue' | 'rating';
      issueHint?: string;
      scopeLocationId?: string;
      rating?: number;
      comment?: string;
      image?: File | null;
    }) => {
      if (!qrCode) return { ok: false as const, error: t('errors:qrCode.invalid') };
      navigator.vibrate?.(payload.mode === 'all_good' ? [30, 80, 30, 80, 60] : [30, 60, 30]);

      if (!online) {
        try {
          await queueFeedback({
            qrCode,
            rating: payload.rating,
            comment: payload.comment || payload.issueHint,
            image: payload.image || undefined,
          });
          setQueuedCount(await getTotalQueueCount());
          return { ok: true as const, queued: true };
        } catch {
          return { ok: false as const, error: t('errors.submitFailed') };
        }
      }

      // Upload photo first if present (skipped for all_good, server rejects images there)
      let imageMeta: {
        imageKey: string;
        imageMimeType: 'image/jpeg' | 'image/png' | 'image/webp';
        imageFileSize: number;
      } | null = null;
      if (payload.image && payload.mode !== 'all_good') {
        imageMeta = await uploadPhoto(payload.image);
        if (!imageMeta) {
          // Warn but don't block, submit without the photo rather than fail the whole report
          toast({
            title: t('errors.photoUploadFailed', 'Photo upload failed'),
            description: t('errors.photoUploadFailedMessage', 'Sending your report without it.'),
          });
        }
      }

      try {
        const body: Record<string, unknown> = {
          qrCode,
          mode: payload.mode,
          issueHint: payload.issueHint,
          scopeLocationId: payload.scopeLocationId,
          rating: payload.rating,
          comment: payload.comment,
        };
        if (imageMeta) Object.assign(body, imageMeta);
        const response = await fetch(`${apiUrl}/api/feedback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const result = await response.json();
        if (!response.ok || !result.success) {
          return { ok: false as const, error: apiErrorText(i18n, result, t('errors.submitFailed')) };
        }
        return { ok: true as const, data: result.data, hadImage: !!imageMeta };
      } catch (err) {
        return {
          ok: false as const,
          error: err instanceof Error ? err.message : t('errors.submitFailed'),
        };
      }
    },
    [apiUrl, i18n, online, qrCode, t, uploadPhoto],
  );

  // ============================================================================
  // Handlers, binary / building / chat / status disclosure
  // ============================================================================

  const handleAllGood = useCallback(async () => {
    // The button itself animates into a "Logged, thanks" reward state for
    // ~1.4s, no toast needed (and the toast would compete with that reward).
    // Errors still raise a toast because the button can't gracefully show
    // failure without adding more states we don't need in the happy path.
    const result = await submitFeedback({ mode: 'all_good' });
    if (result.ok) {
      setData((prev) =>
        prev
          ? {
              ...prev,
              statusItems: prev.statusItems.map((item) => ({
                ...item,
                currentStatus: pickOkValueFor(item),
                lastUpdatedAt: new Date().toISOString(),
              })),
            }
          : prev,
      );
    } else {
      toast({ title: t('toast.error'), description: result.error, variant: 'destructive' });
    }
  }, [submitFeedback, t]);

  const handleIssuePick = useCallback(
    async (option: IssueOption, image: File | null) => {
      // The sheet owns its close timing, it shows a 700ms in-sheet reward
      // before dismissing itself. We only reset the photo-prompt hint and
      // the attached image here, once the submit finishes.
      setIssuePhotoPrompted(false);
      const result = await submitFeedback({ mode: 'issue', issueHint: option.hint, image });
      if (result.ok) {
        markIssueLogged();
        setIssueImage(null);
        // The in-sheet check-mark IS the success feedback for the happy path.
        // Skip the toast so we don't double-reward; keep toast only for error.
      } else {
        toast({ title: t('toast.error'), description: result.error, variant: 'destructive' });
      }
    },
    [submitFeedback, t],
  );

  const handleBuildingPick = useCallback(
    async (category: BuildingCategory) => {
      if (!data) return;
      // The sheet owns its close timing, it shows a 700ms in-sheet reward
      // before dismissing itself. Keep the error toast for failures only.
      const result = await submitFeedback({
        mode: 'issue',
        issueHint: category.hint,
        scopeLocationId: data.building.id,
      });
      if (result.ok) {
        markIssueLogged();
      } else {
        toast({ title: t('toast.error'), description: result.error, variant: 'destructive' });
      }
    },
    [submitFeedback, data, t],
  );

  // StatusGrid tap → open the existing modal (unchanged v1 behaviour)
  const handleItemClick = useCallback((item: StatusItem) => {
    setSelectedItem(item);
    setModalOpen(true);
  }, []);

  const handleStatusSubmit = useCallback(
    async (statusItemId: string, newStatus: string, comment?: string) => {
      if (!qrCode) throw new Error(t('errors:qrCode.invalid'));

      const updateLocal = () =>
        setData((prev) =>
          prev
            ? {
                ...prev,
                statusItems: prev.statusItems.map((item) =>
                  item.id === statusItemId
                    ? { ...item, currentStatus: newStatus, lastUpdatedAt: new Date().toISOString() }
                    : item,
                ),
              }
            : prev,
        );

      if (!online) {
        await queueStatusReport({ qrCode, statusItemId, newStatus, comment });
        setQueuedCount(await getTotalQueueCount());
        updateLocal();
        setConfirmation({
          statusType: selectedItem?.statusType.displayName || selectedItem?.statusType.name || '',
          newStatus,
          taskCreated: false,
        });
        return;
      }

      const response = await fetch(`${apiUrl}/api/status/${qrCode}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statusItemId, newStatus, comment }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(apiErrorText(i18n, result, t('errors:feedback.updateFailed')));
      }
      updateLocal();
      setConfirmation({
        statusType: selectedItem?.statusType.name || '',
        newStatus,
        taskCreated: result.data?.taskCreated || false,
      });
    },
    [apiUrl, online, qrCode, selectedItem, t],
  );

  const handleConfirmationDismiss = useCallback(() => {
    setConfirmation(null);
    setSelectedItem(null);
  }, []);

  // ============================================================================
  // Derived values
  // ============================================================================

  // Rotating example text INSIDE the ask card. Prefer the tenant's real
  // curated prompts so the teaching hint matches what the chips below promise.
  // If no tenant prompts exist, or if they're in a script the visitor isn't
  // reading in right now (e.g. English prompts on a Hebrew page), fall back
  // to the generic locale examples. Showing English copy on an RTL page adds
  // noise and pulls focus.
  const languageFilter = useCallback((text: string) => matchesUILanguage(text, i18n.language), [i18n.language]);
  const examples = useMemo(() => {
    // Prefer the translated `displayQuestion` (when tenant opts into content
    // translation); fall back to the source string. Same language filter
    // applies, if the LLM returned something still outside the UI locale
    // (unlikely but possible), we'd rather show English defaults than mixed
    // scripts next to the ask card.
    const tenantPrompts = (data?.prompts ?? [])
      .map((p) => p.displayQuestion ?? p.question)
      .filter((q) => languageFilter(q))
      .map((q) => `"${q}"`);
    if (tenantPrompts.length > 0) return tenantPrompts;
    return (
      (t('v2.askCard.examples', { returnObjects: true, defaultValue: DEFAULT_EXAMPLES_EN }) as string[]) ||
      DEFAULT_EXAMPLES_EN
    );
  }, [data?.prompts, t, languageFilter]);

  // Suggestion chips ONLY come from tenant-curated RagPrompts. Same language
  // filter as the rotating examples, don't render English chips on a Hebrew
  // page. If nothing matches, no chip row renders.
  const suggestionChips = useMemo(() => {
    const tenantPrompts = (data?.prompts ?? [])
      .map((p) => ({ ...p, resolved: p.displayQuestion ?? p.question }))
      .filter((p) => languageFilter(p.resolved));
    return tenantPrompts.map((p) => ({
      key: p.id,
      icon: p.icon || '💬',
      label: p.resolved,
    }));
  }, [data?.prompts, languageFilter]);

  const issueOptions = useMemo<IssueOption[]>(() => {
    const items = data?.statusItems ?? [];
    // Build one option per tracked item + a generic "something else"
    const fromItems: IssueOption[] = items.map((item) => {
      const displayName = item.statusType.displayName ?? item.statusType.name;
      return {
        key: item.id,
        icon: item.statusType.icon || '⚠️',
        label: t('v2.issueSheet.itemLabel', {
          name: displayName,
          defaultValue: 'Issue with {{name}}',
        }),
        // Hint is sent to the backend as context; keep it stable on the
        // source string so backend tagging doesn't drift by locale.
        hint: `${item.statusType.name.toLowerCase()} issue`,
      };
    });
    const generic: IssueOption = {
      key: '__other',
      icon: '✏️',
      label: t('v2.issueSheet.somethingElse', 'Something else'),
      hint: 'generic',
    };
    return [...fromItems.slice(0, 5), generic];
  }, [data?.statusItems, t]);

  const buildingCategories = useMemo<BuildingCategory[]>(() => {
    const raw =
      (t('v2.buildingSheet.categories', {
        returnObjects: true,
        defaultValue: DEFAULT_BUILDING_CATEGORIES_EN,
      }) as Array<{ key: string; icon: string; label: string }>) || DEFAULT_BUILDING_CATEGORIES_EN;
    return raw.map((c) => ({ ...c, hint: `building: ${c.key}` }));
  }, [t]);

  const unresolvedCount = useMemo(
    () => (data?.statusItems ?? []).filter((i) => !isOkStatus(i)).length,
    [data?.statusItems],
  );

  // ============================================================================
  // Renders, error / loading / happy path
  // ============================================================================

  if (loading) return <FeedbackSkeleton />;

  if (error || !data) {
    const isNotConfigured = errorType === 'NOT_CONFIGURED';
    const isNotFound = errorType === 'NOT_FOUND';
    // Use the richer notFound.* copy when the QR is invalid, same treatment
    // as the root NotFoundPage, so the visitor gets an actionable screen.
    const title = isNotConfigured
      ? t('errors.settingUpTitle')
      : isNotFound
        ? t('notFound.title', { defaultValue: 'Invalid QR Code' })
        : t('errors.qrIssueTitle');
    const message = isNotConfigured
      ? notConfiguredData.tenantName
        ? t('errors.settingUpMessageWithTenant', { tenant: notConfiguredData.tenantName })
        : t('errors.settingUpMessage')
      : isNotFound
        ? t('notFound.description', { defaultValue: 'Please scan a valid TendedLoop QR code.' })
        : error;
    return (
      <div
        className="min-h-[100dvh] flex flex-col items-center justify-center px-5 py-8"
        style={{ background: '#FAFAF7' }}
        data-testid="error-page"
      >
        <div className="absolute top-4 end-4">
          <LanguageSwitcher compact />
        </div>

        <div className="w-full max-w-[440px] text-center animate-fade-in-up">
          {isNotConfigured ? (
            <div className="w-16 h-16 mx-auto rounded-2xl bg-forest/5 flex items-center justify-center mb-5">
              <Building2 className="h-8 w-8 text-forest" aria-hidden="true" />
            </div>
          ) : (
            <div className="w-16 h-16 mx-auto rounded-2xl bg-critical/5 flex items-center justify-center mb-5">
              <AlertCircle className="h-8 w-8 text-critical" aria-hidden="true" />
            </div>
          )}

          <h1 className="text-[24px] font-bold leading-tight tracking-[-0.02em] text-[#0A0E14]">{title}</h1>
          <p className="mt-2 text-[16px] font-medium leading-relaxed text-[#5A6472]" data-testid="error-message">
            {message}
          </p>

          {/* Primary recovery: reload the page. The visitor can also tap the
              language switcher if they need to read this in their language. */}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-[16px] font-semibold text-white transition-transform active:scale-[0.97]"
            style={{ background: 'linear-gradient(135deg, #0E7C7B 0%, #0A5958 100%)' }}
          >
            <RotateCw className="h-4 w-4" aria-hidden="true" />
            {t('notFound.retry', { defaultValue: 'Try Again' })}
          </button>

          {/* v4 audit: invalid-QR recovery hint — Try Again reloads the same
              bad URL, so if the sticker is damaged the visitor needs a clear
              next step. Only show on NOT_FOUND; NOT_CONFIGURED already points
              the visitor to the right place ("location is being prepared"). */}
          {isNotFound && (
            <p className="mt-4 text-[15px] text-[#5A6472] leading-relaxed">
              {t(
                'notFound.fallbackHint',
                'If this code is damaged, look for another sticker nearby or ask facility staff.',
              )}
            </p>
          )}

          {/* Scout CTA, same quiet pattern as NotFoundPage. */}
          <a
            href={`${import.meta.env.VITE_SCOUT_URL || 'https://tendedloop-scout.pages.dev'}/login?utm_source=visitor-pwa&utm_medium=error-cta`}
            className="mt-8 flex items-center gap-3 rounded-xl border border-dashed p-3 transition-all hover:shadow-sm hover:border-solid group"
            style={{ borderColor: '#40916C' }}
          >
            <div
              className="h-9 w-9 flex-shrink-0 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: 'rgba(64,145,108,0.15)' }}
            >
              <Trophy className="h-5 w-5" style={{ color: '#40916C' }} aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0 text-start">
              <p className="text-[16px] font-semibold text-[#0A0E14]">{t('cta.scoutTitle')}</p>
              <p className="text-[15px] text-[#5A6472]">{t('cta.scoutMessage')}</p>
            </div>
            <ArrowRight
              className="h-4 w-4 text-[#5A6472] group-hover:translate-x-0.5 transition-transform"
              aria-hidden="true"
            />
          </a>
        </div>
      </div>
    );
  }

  // Happy path, v2 layout. DirectionProvider sets data-direction on <html>,
  // which directions.css reads to swap the 24 CSS-var palette between
  // ATELIER and CONCIERGE. Default = concierge when tenant.direction is
  // absent (matches the schema default + keeps offline / pre-API renders
  // visually stable).
  return (
    <DirectionProvider direction={data.tenant?.direction}>
      <div
        className="min-h-[100dvh] flex flex-col"
        style={{ background: 'var(--dir-bg-canvas, #FAFAF7)' }}
        data-testid="v2-landing"
      >
        {data.tenant?.brand && <BrandBar brand={data.tenant.brand} fallbackName={data.tenant.name} />}
        {!online && (
          <div className="mx-4 mt-3 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[15px] text-amber-900">
            <WifiOff className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">{t('offline.banner', "You're offline. Updates saved locally.")}</span>
            {queuedCount > 0 && (
              <span className="ms-auto text-[15px] font-medium opacity-70">
                {t('offline.queued', { count: queuedCount, defaultValue: '{{count}} queued' })}
              </span>
            )}
          </div>
        )}

        <main className="mx-auto w-full max-w-[440px] flex-1 px-5">
          {/* Identity, quiet line + strong amenity name. No amenity-icon square.
            Header is relatively-positioned so DirectionAvatar (Atelier monogram)
            can anchor to the top-right corner without affecting layout flow.
            Concierge avatar inlines inside the header instead. */}
          <header className="relative pt-8">
            <DirectionAvatar tenantName={data.tenant?.name ?? ''} />
            <div className="text-[15px] font-medium" style={{ color: 'var(--dir-ink-muted)' }}>
              <span lang={data.building.nameLocale} dir="auto">
                {data.building.displayName ?? data.building.name}
              </span>
              {/* Only surface the level number when it's a meaningful positive floor.
                Level 0 reads as "nothing" to a visitor; basements (-1, -2, …) are
                better identified by the amenity name itself ("Parking A"). */}
              {typeof data.location.level === 'number' && data.location.level > 0 && (
                <span>
                  {' · '}
                  {t('common.level', { level: data.location.level, defaultValue: 'Level {{level}}' })}
                </span>
              )}
            </div>
            <h1
              className="mt-1 text-[28px] font-bold leading-[1.1] tracking-[-0.02em]"
              style={{
                color: 'var(--dir-ink-primary)',
                fontFamily: 'var(--dir-font-display)',
                fontStyle: 'var(--dir-display-italic)' as React.CSSProperties['fontStyle'],
                fontWeight: 'var(--dir-display-weight)' as unknown as React.CSSProperties['fontWeight'],
              }}
              lang={data.amenity.nameLocale}
              dir="auto"
            >
              {data.amenity.displayName ?? data.amenity.name}
            </h1>
            {/* Accessibility chip, render a small ♿ pill when the amenity name
              indicates an accessible route/facility. Name-based detection is
              pragmatic: no dedicated schema flag exists yet, but the admin-
              entered name in the seed consistently flags with the locale word
              for "accessible" (נגיש / accessible / ذوي الإعاقة). */}
            {/נגיש|accessible|♿|вариант с доступом|متاح|زוגל/i.test(data.amenity.name) && (
              <span
                className="mt-2 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-[15px] font-semibold text-blue-800"
                aria-label={t('v2.accessible', 'Accessible')}
              >
                <span aria-hidden="true">♿</span>
                {t('v2.accessible', 'Accessible')}
              </span>
            )}
            <p className="mt-4 text-[17px] font-medium leading-snug text-[#5A6472]">{greeting.lead}</p>

            {/* Trust microcopy — unanimous finding across all v1/v2/v3 audits. In
              healthcare and workplace contexts visitors hesitate to report an
              issue if they can't tell whether this is linked to their account.
              Deliberately small, muted, and one-line. */}
            <p className="mt-2 inline-flex items-center gap-1.5 text-[15px] font-medium text-[#5A6472]">
              {/* v4 audit: ShieldCheck reads as "cybersecurity/antivirus",
                Lock is the universal privacy metaphor. */}
              <Lock className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              {t('v2.anonymity', 'Anonymous — no login needed')}
            </p>

            {/* Parking anchor, shows on amenities flagged as anchorable (parking, etc.) */}
            {data.tenant && data.amenity.amenityType.anchorable && (
              <div onBlur={() => setAnchorTick((x) => x + 1)}>
                <AnchorChip
                  tenantId={data.tenant.id}
                  tenantName={data.tenant.name}
                  buildingId={data.building.id}
                  buildingName={data.building.displayName ?? data.building.name}
                  amenityId={data.amenity.id}
                  amenityName={data.amenity.displayName ?? data.amenity.name}
                  locationName={data.location.displayName ?? data.location.name}
                  level={data.location.level}
                  ttlHours={data.parking?.ttlHours ?? 12}
                />
              </div>
            )}
          </header>

          {/* Optional parking-recall pin sits above the hero so a returning
            visitor's "where did I park?" use case stays one tap away. */}
          {parkedSpot && parkedSpot.amenityId !== data.amenity.id && (
            <div className="mt-5">
              <button
                type="button"
                onClick={() => setRecallOpen(true)}
                className="flex w-full items-center gap-2 rounded-2xl border border-[#E8EAED] bg-white px-4 py-2.5 text-start transition-transform active:scale-[0.99]"
              >
                <span className="text-[18px]" aria-hidden="true">
                  🅿️
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block truncate text-[15px] font-semibold text-[#0A0E14]">
                    {t('v2.recall.pinned', 'Parked at {{name}}', { name: parkedSpot.amenityName })}
                  </span>
                  <span className="block truncate text-[15px] font-medium text-[#5A6472]">
                    {relativeTime(parkedSpot.ts, i18n.language)}
                    {' · '}
                    {t('v2.recall.tapToFind', 'tap to find your car')}
                  </span>
                </span>
              </button>
            </div>
          )}

          {/* FORESIGHT HERO — Lobby scene='foresight'. Pre-empts the question
            most recently asked at this place, with a cited KB-grounded answer.
            Single hero (Apple Wallet rule): when this is showing, the memory
            band and greeting are suppressed. Dismiss link collapses back to
            the Ask scene without a page reload. */}
          {showForesight && landing?.anticipated && (
            <ForesightHero
              anticipated={landing.anticipated}
              onDismiss={dismissForesight}
              onFollowup={(q) => {
                visitorInteractedRef.current = true;
                setChatSeed(q);
                setChatOpen(true);
              }}
            />
          )}

          {/* Memory cue — only when AI-composed landing has a memory.cue AND we
            are NOT in foresight scene (single-hero rule). Small dimmed italic
            line ABOVE the hero. aria-live polite so screen readers announce
            on swap. */}
          {!showForesight && landing?.memory?.cue && (
            <div
              className="mt-6 mb-2 text-center text-[14px] italic text-[#8A93A0] animate-fade-in"
              aria-live="polite"
              dir="auto"
            >
              {landing.memory.cue}
            </div>
          )}

          {/* HERO — Direction 1 "Asking Place".
            Conversation is the primary act, not operational feedback. When
            the AI-composed landing has arrived, the greeting + placeholder
            come from it; otherwise the static fallback ("What do you want
            to know?") renders. aria-live so screen readers announce the
            swap. Suppressed when Foresight is showing. */}
          {!showForesight && (
            <section className="mt-10 mb-6 text-center" aria-live="polite">
              <h2 className="text-[22px] font-bold leading-[1.2] tracking-[-0.015em] text-[#0A0E14]" dir="auto">
                {landing?.hero.greeting ?? t('v2.hero.askPrompt', 'What do you want to know?')}
              </h2>
            </section>
          )}
          <button
            type="button"
            onClick={() => {
              visitorInteractedRef.current = true;
              setChatSeed(null);
              setChatOpen(true);
            }}
            aria-label={t('v2.askCard.aria', 'Ask a question about this place')}
            className="flex w-full items-center gap-3 rounded-full border border-[#E8EAED] bg-white px-5 py-3 text-start transition-transform active:scale-[0.99]"
            style={{ boxShadow: '0 12px 28px -16px rgba(10,14,20,0.15)' }}
          >
            <span
              key={showForesight ? 'foresight-fallback' : (landing?.placeholder ?? examples[0] ?? 'placeholder')}
              className="flex-1 min-w-0 truncate text-[15px] font-medium text-[#5A6472] animate-fade-in"
              dir="auto"
            >
              {showForesight
                ? t('v3.foresight.askSomethingElse', '…or ask something else')
                : (landing?.placeholder ??
                  examples[0] ??
                  t('v2.askCard.placeholder', 'Ask me anything about this place'))}
            </span>
            <span
              aria-hidden="true"
              className="h-11 w-11 flex-shrink-0 rounded-full flex items-center justify-center"
              style={{ background: '#0A0E14', color: '#FAFAF7' }}
            >
              <Mic className="h-4 w-4" />
            </span>
          </button>

          {/* Suggestion strip — when AI landing has arrived, render its
            chips (server appends the report-chip last). Otherwise render
            the static tenant prompts + manual report chip.
            In Foresight scene only the report chip survives — ask/find
            chips become redundant once the surface has already answered. */}
          <div className="mt-4 flex flex-col gap-1">
            {landing?.chips ? (
              (showForesight ? landing.chips.filter((c) => c.intent === 'report') : landing.chips).map((chip, idx) => (
                <button
                  key={`${chip.intent}-${idx}-${chip.label}`}
                  type="button"
                  onClick={() => {
                    visitorInteractedRef.current = true;
                    if (chip.intent === 'report') {
                      setIssueOpen(true);
                    } else {
                      setChatSeed(chip.seed ?? chip.label);
                      setChatOpen(true);
                    }
                  }}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-start transition-colors hover:bg-white"
                >
                  {chip.icon ? (
                    <span aria-hidden="true" className="text-[16px]">
                      {chip.icon}
                    </span>
                  ) : chip.intent === 'report' ? (
                    <Wrench className="h-4 w-4 flex-shrink-0 text-[#5A6472]" aria-hidden="true" />
                  ) : (
                    <span aria-hidden="true" className="text-[16px]">
                      💬
                    </span>
                  )}
                  <span className="flex-1 min-w-0 truncate text-[15px] font-medium text-[#0A0E14]" dir="auto">
                    {chip.label}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-[#5A6472]" aria-hidden="true" />
                </button>
              ))
            ) : (
              <>
                {suggestionChips.slice(0, 3).map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => {
                      visitorInteractedRef.current = true;
                      setChatSeed(chip.label);
                      setChatOpen(true);
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-start transition-colors hover:bg-white"
                  >
                    <span aria-hidden="true" className="text-[16px]">
                      {chip.icon}
                    </span>
                    <span className="flex-1 min-w-0 truncate text-[15px] font-medium text-[#0A0E14]" dir="auto">
                      {chip.label}
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-[#5A6472]" aria-hidden="true" />
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    visitorInteractedRef.current = true;
                    setIssueOpen(true);
                  }}
                  data-has-issue={hasIssueThisSession ? 'true' : 'false'}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-start transition-colors hover:bg-white"
                >
                  <Wrench
                    className={cn('h-4 w-4 flex-shrink-0', hasIssueThisSession ? 'text-[#991B1B]' : 'text-[#5A6472]')}
                    aria-hidden="true"
                  />
                  <span
                    className={cn(
                      'flex-1 min-w-0 truncate text-[15px] font-medium',
                      hasIssueThisSession ? 'text-[#991B1B]' : 'text-[#0A0E14]',
                    )}
                    dir="auto"
                  >
                    {hasIssueThisSession
                      ? t('v2.binary.issueLoggedSub', 'Logged. Tap to add another.')
                      : t('v2.hero.reportChip', "Something's off — report it")}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-[#5A6472]" aria-hidden="true" />
                </button>
              </>
            )}
          </div>

          {/* Meta, hairline-separated rows for power users.
            Hide the disclosure entirely when there's nothing to track, a
            collapsed "all clear" header over zero items is pure noise. */}
          {data.statusItems.length > 0 && (
            <div className="mt-8 border-t border-[#E8EAED]">
              <button
                type="button"
                onClick={() => setStatusOpen((x) => !x)}
                aria-expanded={statusOpen}
                className="flex w-full items-center justify-between py-4 text-start"
              >
                <span className="text-[15px] font-medium text-[#0A0E14]">
                  {t('v2.track.label', 'Tracked items')}
                  <span className="ms-2 text-[#5A6472] font-normal">
                    {unresolvedCount > 0
                      ? t('v2.track.summaryIssues', { count: unresolvedCount, defaultValue: '{{count}} open' })
                      : t('v2.track.summaryAllOk', 'all clear')}
                  </span>
                </span>
                <ChevronDown
                  className={cn('h-5 w-5 text-[#5A6472] transition-transform', statusOpen && 'rotate-180')}
                  aria-hidden="true"
                />
              </button>
              {statusOpen && data.statusItems.length > 0 && (
                <div className="pb-4">
                  <StatusGrid statusItems={data.statusItems} onItemClick={handleItemClick} />
                </div>
              )}
            </div>
          )}

          <div className="border-t border-[#E8EAED]">
            <button
              type="button"
              onClick={() => setBuildingOpen(true)}
              className="block w-full py-4 text-start text-[15px] font-medium transition-colors"
              // Uses --tl-accent so the operator's brand color shines through
              // when they've opted in. Default is #0A5958 (AAA on cream).
              style={{ color: 'var(--tl-accent)' }}
            >
              {t('v2.buildingLink', 'Report something elsewhere →')}
            </button>
          </div>

          <div className="flex items-center justify-between border-t border-[#E8EAED] py-4 text-[15px] text-[#5A6472]">
            <LanguageSwitcher compact />
            {data.showPoweredBy && <span>{t('footer.poweredByTendedLoop', 'Powered by TendedLoop')}</span>}
          </div>
        </main>

        {/* Overlays */}
        <ChatSheet
          open={chatOpen}
          qrCode={qrCode || ''}
          apiUrl={apiUrl}
          buildingName={data.building.displayName ?? data.building.name}
          seed={chatSeed}
          anchorAmenityId={parkedSpot && parkedSpot.amenityId !== data.amenity.id ? parkedSpot.amenityId : null}
          onClose={() => setChatOpen(false)}
          onReportIssue={() => {
            setChatOpen(false);
            setIssueImage(null);
            setIssuePhotoPrompted(false);
            setIssueOpen(true);
          }}
        />
        <IssueSheet
          open={issueOpen}
          options={issueOptions}
          attachedImage={issueImage}
          onImageChange={setIssueImage}
          photoPrompted={issuePhotoPrompted}
          onClose={() => {
            setIssueOpen(false);
            setIssuePhotoPrompted(false);
            setIssueImage(null);
          }}
          onSubmit={handleIssuePick}
        />
        <BuildingSheet
          open={buildingOpen}
          buildingName={data.building.displayName ?? data.building.name}
          categories={buildingCategories}
          onClose={() => setBuildingOpen(false)}
          onSubmit={handleBuildingPick}
        />
        {parkedSpot && (
          <ParkingRecallOverlay
            open={recallOpen}
            onClose={() => setRecallOpen(false)}
            spot={parkedSpot}
            fromQrCode={qrCode || ''}
            fromAmenityId={data.amenity.id}
            apiUrl={apiUrl}
            outdoorMapsDestination={[data.tenant?.name, data.building.name].filter(Boolean).join(' · ')}
            onCleared={() => {
              setRecallOpen(false);
              setParkedSpot(null);
              setAnchorTick((x) => x + 1);
            }}
          />
        )}

        {/* Status update modal, unchanged from v1 */}
        {selectedItem && (
          <StatusUpdateModal
            open={modalOpen && !confirmation}
            onOpenChange={(o) => {
              setModalOpen(o);
              if (!o) setSelectedItem(null);
            }}
            item={selectedItem}
            onSubmit={handleStatusSubmit}
          />
        )}
        {confirmation && (
          <StatusConfirmation
            statusType={confirmation.statusType}
            newStatus={confirmation.newStatus}
            taskCreated={confirmation.taskCreated}
            onDismiss={handleConfirmationDismiss}
          />
        )}
      </div>
    </DirectionProvider>
  );
}

// ============================================================================
// Chip row with overflow-aware fade
// ============================================================================

interface SuggestionChip {
  key: string;
  icon: string;
  label: string;
}

function SuggestionChipsRow({
  chips,
  ariaLabel,
  onPick,
  rtl,
}: {
  chips: SuggestionChip[];
  ariaLabel: string;
  onPick: (label: string) => void;
  rtl?: boolean;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollWidth > el.clientWidth + 1);
    check();
    // Chip text / icons can reflow on font/language changes; watch for that.
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [chips.length]);

  return (
    <div className="relative -mx-5 mt-3">
      <div
        ref={scrollerRef}
        className="flex gap-2 overflow-x-auto px-5 pb-1 scrollbar-hide"
        role="list"
        aria-label={ariaLabel}
      >
        {chips.map((c) => (
          <button
            key={c.key}
            type="button"
            role="listitem"
            onClick={() => onPick(c.label)}
            className="flex min-h-[48px] flex-shrink-0 items-center whitespace-nowrap rounded-full border border-[#E8EAED] bg-white px-4 py-3 text-[15px] font-medium text-[#0A0E14] transition-transform active:scale-[0.97]"
          >
            <span className="me-1.5 text-[16px]" aria-hidden="true">
              {c.icon}
            </span>
            {c.label}
          </button>
        ))}
      </div>
      {/* Overflow-edge fade, sits on the logical `end` side. In LTR the
          gradient goes right→left (solid on the right edge). In RTL the chip
          row scrolls the opposite direction and the `end-0` position resolves
          to the left, so the gradient must flip too or the fade appears on
          the wrong side and the clip stays visible. */}
      {overflows && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute end-0 top-0 h-full w-8"
          style={{
            background: rtl
              ? 'linear-gradient(to right, #FAFAF7, transparent)'
              : 'linear-gradient(to left, #FAFAF7, transparent)',
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Fallback copy & helpers
// ============================================================================

// Scripts we can detect in tenant-curated prompts to match them against the
// visitor's current UI language. For Latin-script UIs (en, es, fr, de, it,
// pt-BR) we accept anything, the prompts don't have to be in the exact UI
// language, they just need to not be in a script the visitor can't read.
const NON_LATIN_SCRIPTS: Record<string, RegExp> = {
  he: /[\u05D0-\u05EA]/,
  yi: /[\u05D0-\u05EA]/,
  ar: /[\u0600-\u06FF]/,
  am: /[\u1200-\u137F]/,
  ru: /[\u0400-\u04FF]/,
  // Kana, or kanji (the only CJK UI language is Japanese).
  ja: /[\u3040-\u30FF\u4E00-\u9FFF]/,
};

/**
 * True if the given text can reasonably be read in the UI language. Tenant
 * prompts are free-form, an English prompt on a Hebrew page is visual noise
 * that pulls focus from the primary flow, so we hide it and fall back to the
 * locale's generic examples.
 */
function matchesUILanguage(text: string, lang: string): boolean {
  const base = lang?.split('-')[0] ?? 'en';
  const rx = NON_LATIN_SCRIPTS[base];
  if (!rx) return true;
  return rx.test(text);
}

// Used only as a last-resort fallback for the rotating-example text inside
// the ask card when the locale file isn't available. These are not chips ,
// they don't claim the building can answer, they teach the interaction pattern.
const DEFAULT_EXAMPLES_EN = [
  '"When was this last cleaned?"',
  '"Where\'s the nearest lactation room?"',
  '"What time does the building close?"',
  '"What\'s the wifi password?"',
];

const DEFAULT_BUILDING_CATEGORIES_EN = [
  { key: 'electrical', icon: '⚡️', label: 'Electrical' },
  { key: 'plumbing', icon: '💧', label: 'Plumbing' },
  { key: 'hvac', icon: '❄️', label: 'HVAC' },
  { key: 'lifts', icon: '🛗', label: 'Lifts' },
  { key: 'security', icon: '🛡️', label: 'Security' },
  { key: 'cleaning', icon: '🧹', label: 'Cleaning' },
  { key: 'damage', icon: '🔨', label: 'Damage' },
  { key: 'access', icon: '🚪', label: 'Access' },
  { key: 'other', icon: '✏️', label: 'Other' },
];

// Canonical OK value for a tracked item, prefers tree-provided options,
// falls back to the legacy per-category strings.
function pickOkValueFor(item: StatusItem): string {
  const opts = item.statusOptions;
  if (opts && opts.length > 0) {
    const ok = opts.find((o) => !o.isBadStatus);
    if (ok) return ok.value;
  }
  switch (item.statusType.category) {
    case 'CONSUMABLE':
      return 'OK';
    case 'EQUIPMENT':
      return 'WORKING';
    case 'CLEANLINESS':
      return 'CLEAN';
    case 'COMFORT':
      return 'COMFORTABLE';
    default:
      return 'OK';
  }
}

function isOkStatus(item: StatusItem): boolean {
  const s = item.currentStatus;
  if (s === 'OK' || s === 'WORKING' || s === 'CLEAN' || s === 'COMFORTABLE') return true;
  const opt = item.statusOptions?.find((o) => o.value === s);
  if (opt) return !opt.isBadStatus;
  return false;
}
